/**
 * Navigation model for the authenticated shell.
 *
 * `personas` is a UI affordance only — the gateway RBAC table
 * (`services/gateway/src/rbac-table.ts`) is the authority on access. Hiding a
 * link the user cannot use keeps the sideline UI uncluttered; it is not a
 * security control.
 */
import { APP_VIEWS } from './constants';
import type { Persona } from './personas';

export interface NavItem {
  id: string;
  label: string;
  /** Short label for the phone tab bar. */
  shortLabel: string;
  /** Emoji stand-in for the icon set; replaced by the icon system in a later slice. */
  icon: string;
  /** View slug under `/app/{tenant_slug}` — empty string is the portal home. */
  view: string;
  personas: readonly Persona[];
  /** Appears in the 5-slot phone tab bar. */
  isPhoneTab?: boolean;
  /**
   * Sidebar section. 18 links in one flat list is a wall; grouping them into
   * labelled sections is what makes the rail scannable. Order of first
   * appearance in NAV_ITEMS sets the section order.
   */
  group: NavGroup;
}

/** Sidebar sections, in render order. */
export const NAV_GROUPS = ['Game day', 'Team', 'Administration'] as const;
export type NavGroup = (typeof NAV_GROUPS)[number];

const ALL_TENANT_PERSONAS: readonly Persona[] = [
  'team_admin',
  'offensive_coordinator',
  'defensive_coordinator',
  'coach',
  'primary_stat_operator',
  'general_stat_operator',
];

const COACHING: readonly Persona[] = [
  'team_admin',
  'offensive_coordinator',
  'defensive_coordinator',
  'coach',
];

export const NAV_ITEMS: readonly NavItem[] = [
  {
    id: 'home',
    group: 'Game day',
    label: 'Home',
    shortLabel: 'Home',
    icon: '⌂',
    view: APP_VIEWS.home,
    personas: ALL_TENANT_PERSONAS,
  },
  {
    id: 'coaches-highlight',
    group: 'Game day',
    label: 'Coaches Highlight',
    shortLabel: 'Coaching',
    icon: '◎',
    view: APP_VIEWS.coachesHighlight,
    personas: COACHING,
    isPhoneTab: true,
  },
  {
    id: 'live-video',
    group: 'Game day',
    label: 'Live Video',
    shortLabel: 'Video',
    icon: '▶',
    view: APP_VIEWS.liveVideo,
    personas: COACHING,
  },
  {
    id: 'game-summary',
    group: 'Game day',
    label: 'Game Summary',
    shortLabel: 'Summary',
    icon: '▤',
    view: APP_VIEWS.gameSummary,
    personas: ALL_TENANT_PERSONAS,
  },
  {
    id: 'play-entry',
    group: 'Game day',
    label: 'Play Entry',
    shortLabel: 'Entry',
    icon: '✎',
    view: APP_VIEWS.playEntry,
    personas: ALL_TENANT_PERSONAS,
    isPhoneTab: true,
  },
  {
    id: 'play-archive',
    group: 'Game day',
    label: 'Play Archive',
    shortLabel: 'Archive',
    icon: '≡',
    view: APP_VIEWS.playArchive,
    personas: ALL_TENANT_PERSONAS,
    isPhoneTab: true,
  },
  {
    id: 'stat-tracker',
    group: 'Game day',
    label: 'Stat Tracker',
    shortLabel: 'Stats',
    icon: '∑',
    view: APP_VIEWS.statTracker,
    personas: ALL_TENANT_PERSONAS,
  },
  {
    id: 'playbook',
    group: 'Game day',
    label: 'Playbook',
    shortLabel: 'Playbook',
    icon: '⛨',
    view: APP_VIEWS.playbook,
    // Stat operators are included: they get the same page read-only, and Play
    // Entry autofill only makes sense if they can see the library
    // (FR-PLAYBOOK-05). `fanatic` is absent — no playbook access at all.
    personas: ALL_TENANT_PERSONAS,
    isPhoneTab: true,
  },
  {
    id: 'events',
    group: 'Game day',
    label: 'Events',
    shortLabel: 'Events',
    icon: '⏱',
    view: APP_VIEWS.events,
    personas: ALL_TENANT_PERSONAS,
  },
  {
    id: 'roster',
    group: 'Team',
    label: 'Roster',
    shortLabel: 'Roster',
    icon: '☗',
    view: APP_VIEWS.roster,
    personas: ALL_TENANT_PERSONAS,
  },
  {
    id: 'team-users',
    group: 'Team',
    label: 'Team Users',
    shortLabel: 'Users',
    icon: '☰',
    view: APP_VIEWS.teamUsers,
    personas: ['team_admin'],
  },
  {
    id: 'team-fanatics',
    group: 'Team',
    label: 'Fanatics',
    shortLabel: 'Fanatics',
    icon: '📣',
    view: APP_VIEWS.teamFanatics,
    // Follow mode, invites and revocations are tenant-wide access decisions;
    // every endpoint behind the page is admin-scoped.
    personas: ['team_admin'],
  },
  {
    id: 'team-stat-fields',
    group: 'Team',
    label: 'Custom Stats',
    shortLabel: 'Stats',
    icon: '＃',
    view: APP_VIEWS.teamStatFields,
    personas: ['team_admin'],
  },
  {
    id: 'team-integrations',
    group: 'Administration',
    label: 'Hudl',
    shortLabel: 'Hudl',
    icon: '⇄',
    view: APP_VIEWS.teamIntegrations,
    // Hudl export writes team-wide data to a file; the endpoints behind the
    // page are admin-scoped and Varsity+.
    personas: ['team_admin'],
  },
  {
    id: 'team-identity-providers',
    group: 'Administration',
    label: 'Single Sign-On',
    shortLabel: 'SSO',
    icon: '⚿',
    view: APP_VIEWS.teamIdentityProviders,
    // SAML/SCIM federation is Elite-tier and admin-scoped; the page itself
    // renders the upgrade CTA when the tenant's plan does not include it.
    personas: ['team_admin'],
  },
  {
    id: 'billing',
    group: 'Administration',
    label: 'Billing',
    shortLabel: 'Billing',
    icon: '▦',
    view: APP_VIEWS.billing,
    // Plan changes, invoices and payment methods are admin-only; the billing
    // endpoints are admin-scoped, so nobody else would get past a 403.
    personas: ['team_admin'],
  },
  {
    id: 'team-settings',
    group: 'Administration',
    label: 'Team Settings',
    shortLabel: 'Team',
    icon: '🏈',
    view: APP_VIEWS.teamSettings,
    // Video provider, support routing and the Success Measure rules are
    // tenant-wide configuration; every endpoint behind the page is admin-scoped.
    personas: ['team_admin'],
  },
  {
    id: 'settings',
    group: 'Administration',
    label: 'Settings',
    shortLabel: 'Settings',
    icon: '⚙',
    view: APP_VIEWS.settings,
    personas: ALL_TENANT_PERSONAS,
    isPhoneTab: true,
  },
];

export function navItemsFor(persona: Persona): readonly NavItem[] {
  return NAV_ITEMS.filter((item) => item.personas.includes(persona));
}

/**
 * Nav items bucketed into sidebar sections, preserving NAV_ITEMS order and
 * dropping sections the persona cannot see (a stat operator has no
 * Administration group at all, so no empty heading renders).
 */
export function navGroupsFor(
  persona: Persona
): readonly { group: NavGroup; items: readonly NavItem[] }[] {
  const items = navItemsFor(persona);
  return NAV_GROUPS.map((group) => ({
    group,
    items: items.filter((item) => item.group === group),
  })).filter((section) => section.items.length > 0);
}

/** The five phone tabs, in order (ux-design/responsive.md). */
export function phoneTabsFor(persona: Persona): readonly NavItem[] {
  return navItemsFor(persona)
    .filter((item) => item.isPhoneTab === true)
    .slice(0, 5);
}
